/**
 * The global state selectors
 */

import { createSelector } from 'reselect';
import idx from 'idx.macro';
import { makeSelectSaveSearch } from '../../Paradigms/SaveSearch/modules/selectors';
import { AppealStatus } from 'containers/Appeals/modules/types';
import { AdjustmentStatus } from 'containers/Claims/PrepayClaimsPage/modules/types';

const selectApp = (state) => idx(state, (_) => _.app);
const selectRoute = (state) => idx(state, (_) => _.route);

const makeSelectLocation = () =>
  createSelector(selectRoute, (routeState) => idx(routeState, (_) => _.location));

const makeSelectMasthead = () =>
  createSelector(selectApp, (appState) => {
    // const auth = useAuth();
    let productMenuItems = idx(appState, (_) => _.user.apps) || [];
    const aud = idx(appState, (_) => _.user.aud);
    if (!Array.isArray(productMenuItems)) {
      productMenuItems = [productMenuItems];
    }
    // const currentProduct = productMenuItems.find((app) => app.uri === aud);
    const mappedProductMenuItems = [];
    const authenticated = false; //auth.isAuthenticated;
    return {
      navMenuItems: idx(appState, (_) => _.navMenuItems),
      productMenuItems: mappedProductMenuItems,
      currentProductName: 'Prepayment Insight & Review',
      userName: `${idx(appState, (_) => _.user.given_name)} ${idx(
        appState,
        (_) => _.user.family_name,
      )}`,
      avatarImage: idx(appState, (_) => _.user.photo),
      // probably a more elegant way to do this... in reducer? in index?
      supportMenuItems: authenticated ? idx(appState, (_) => _.supportMenuItems) : [],
      avatarMenuItems: authenticated ? idx(appState, (_) => _.avatarMenuItems) : [],
      logoRedirect: idx(appState, (_) => _.logoRedirect),
    };
  });

const makeSelectLeftStrip = () =>
  createSelector(selectApp, (appState) => ({
    multiSelectGroupItems: idx(appState, (_) => _.multiSelectGroupItems),
    multiSelectGroupOpenIDs: idx(appState, (_) => _.multiSelectGroupOpenIDs),
    multiSelectGroupCheckedIDs: idx(appState, (_) => _.multiSelectGroupCheckedIDs),
    singleSelectGroupItems: idx(appState, (_) => _.singleSelectGroupItems),
    singleSelectIndex: idx(appState, (_) => _.singleSelectIndex),
    openSingleGroupIndices: idx(appState, (_) => _.openSingleGroupIndices),
    leftStripAnimStates: idx(appState, (_) => _.leftStripAnimStates),
    leftStripAnimState: idx(appState, (_) => _.leftStripAnimState),
    leftStripWasOpen: idx(appState, (_) => _.leftStripWasOpen),
    isAppealsSelected: idx(appState, (_) => _.isAppealsSelected),
    isInquiriesSelected: idx(appState, (_) => _.isInquiriesSelected),
    isCreateInvoiceSelected: idx(appState, (_) => _.isCreateInvoiceSelected),
    isInvoiceSearchSelected: idx(appState, (_) => _.isInvoiceSearchSelected),
    isMyRecentActivitiesSelected: idx(appState, (_) => _.isMyRecentActivitiesSelected),
    isRulesSelected: idx(appState, (_) => _.isRulesSelected),
    isReasonCodesSelected: idx(appState, (_) => _.isReasonCodesSelected),
    isDocumentReferencesSelected: idx(appState, (_) => _.isDocumentReferencesSelected),
    isClientsSelected: idx(appState, (_) => _.isClientsSelected),
    isAppealResponsesSelected: idx(appState, (_) => _.isAppealResponsesSelected),
    isActivityLogSelected: idx(appState, (_) => _.isActivityLogSelected),
  }));

const makeSelectLocationState = () => {
  let prevRoutingState;
  let prevRoutingStateJS;

  return (state) => {
    const routingState = idx(state, (_) => _.route); // or state.route

    if (!routingState.equals(prevRoutingState)) {
      prevRoutingState = routingState;
      prevRoutingStateJS = routingState.toJS();
    }

    return prevRoutingStateJS;
  };
};

const makeSelectUser = () => createSelector(selectApp, (appState) => appState.user);
const makeSelectActiveAssigneeList = () =>
  createSelector(selectApp, (state) => {
    const data = idx(state, (_) => _.assigneeList);
    const reducedData = data
      ?.filter((ele) => ele.isActive)
      .map((ele) => `${ele.firstName} ${ele.lastName}`);
    return reducedData;
  });

const makeSelectActiveInquiryAssigneeList = () =>
  createSelector(selectApp, (state) => {
    const data = idx(state, (_) => _.assigneeList);
    const reducedData = data
      ?.filter((ele) => ele.isActive)
      .map((ele) =>
        !!ele.userAccountName
          ? `${ele.firstName} ${ele.lastName} (${ele.userAccountName})`
          : `${ele.firstName} ${ele.lastName}`,
      );
    return reducedData;
  });

const selectAllAssigneeList = (state) => idx(state, (_) => _.app.assigneeList);
const makeSelectAllAssigneeList = () =>
  createSelector(selectApp, (state) => {
    const data = idx(state, (_) => _.assigneeList);
    const reducedData = data.map((ele) => `${ele.firstName} ${ele.lastName}`);
    return reducedData;
  });
const makeSelectAppealStatuses = () =>
  createSelector(selectApp, (state) => {
    let appealStatuses = idx(state, (_) => _.appealStatuses) || [];
    if (appealStatuses) {
      appealStatuses = appealStatuses.map((ele: AppealStatus) => ({
        label: ele.description,
        value: ele.appealStatusID,
        count: ele.count,
        hidden: ele.hidden,
      }));
    }
    return appealStatuses;
  });

const selectAuthModalOpen = (state) => idx(state, (_) => _.app.authModalOpen);

const selectHasUserAcknowledged = (state) => idx(state, (_) => _.app.hasUserAcknowledged);
const selectSelectedLeftNavIndex = (state) => idx(state, (_) => _.app.singleSelectIndex);
const makeSelectNotifications = () =>
  createSelector(selectApp, (appState) => appState.notifications);

const makeSelectOpenAnimationActive = () =>
  createSelector(selectApp, (appState) => appState.openAnimationActive);
const makeSelectCloseAnimationActive = () =>
  createSelector(selectApp, (appState) => appState.closeAnimationActive);
const makeSelectNotificationVisible = () =>
  createSelector(selectApp, (appState) => appState.notificationVisible);
const makeSelectNotificationMessage = () =>
  createSelector(selectApp, (appState) => appState.notificationMessage);
const makeSelectNotificationType = () =>
  createSelector(selectApp, (appState) => appState.notificationType);
const selectAppealStatuses = (state) => idx(state, (_) => _.app.appealStatuses);

export {
  selectApp,
  makeSelectMasthead,
  makeSelectLeftStrip,
  makeSelectLocationState,
  makeSelectLocation,
  selectRoute,
  makeSelectUser,
  selectAuthModalOpen,
  selectHasUserAcknowledged,
  makeSelectNotifications,
  makeSelectSaveSearch,
  makeSelectOpenAnimationActive,
  makeSelectCloseAnimationActive,
  makeSelectNotificationVisible,
  makeSelectNotificationMessage,
  makeSelectNotificationType,
  selectSelectedLeftNavIndex,
  makeSelectActiveAssigneeList,
  makeSelectActiveInquiryAssigneeList,
  makeSelectAllAssigneeList,
  selectAllAssigneeList,
  makeSelectAppealStatuses,
  selectAppealStatuses,
};
